import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchForests, forestPath, isGroupForest, CURRENT_YEAR } from "../forests";

const MEDALS = ["🥇", "🥈", "🥉"];

function Forests() {
  const [forests, setForests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("trees"); // "trees" (all time) or "treesThisYear"

  useEffect(() => {
    fetchForests()
      .then(setForests)
      .catch((e) => {
        console.error("Forests:", e);
        setError("Couldn't load the forests. Please refresh.");
      })
      .finally(() => setLoading(false));
  }, []);

  const rank = (list) => [...list].sort((a, b) => b[sortBy] - a[sortBy] || b.trees - a.trees || a.name.localeCompare(b.name));
  const groups = rank(forests.filter((f) => isGroupForest(f.name)));
  const teams = rank(forests.filter((f) => !isGroupForest(f.name)));
  const total = forests.reduce((s, f) => s + f.trees, 0);

  const leaderboard = (list) => {
    if (!list.length) return <p className="text-gray-600 mb-3">New group forests are on their way.</p>;
    const top = Math.max(1, ...list.map((f) => f[sortBy]));
    return (
      <ol className="space-y-3">
        {list.map((f, i) => {
          const n = f[sortBy];
          return (
            <li key={f.id}>
              <Link
                to={forestPath(f)}
                className="block bg-white rounded-xl shadow hover:shadow-md transition-shadow p-4"
              >
                <div className="flex items-center gap-3">
                  <span className="w-10 text-center text-xl font-bold text-sritaru-700 shrink-0">
                    {n > 0 && i < 3 ? MEDALS[i] : i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold text-gray-800 truncate">{f.name}</span>
                      <span className="font-bold text-sritaru-800 whitespace-nowrap">
                        {n.toLocaleString()} <span className="text-xs font-normal text-gray-500">trees</span>
                      </span>
                    </div>
                    <div className="h-2 bg-sritaru-100 rounded-full mt-2 overflow-hidden">
                      <div
                        className="h-full bg-sritaru-500 rounded-full"
                        style={{ width: `${Math.max(n > 0 ? 2 : 0, (n / top) * 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {f.donors === 0
                        ? "New forest: plant the first tree"
                        : `${f.donors} ${f.donors === 1 ? "dedication" : "dedications"}`}
                      {f.pledged > 0 && ` · ${f.pledged.toLocaleString()} more pledged`}
                    </p>
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ol>
    );
  };

  return (
    <div className="py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-sritaru-600 to-sritaru-700 text-white rounded-xl shadow-lg p-8 mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Group Forests &#127795;</h1>
          <p className="text-sritaru-100 max-w-xl mx-auto">
            Your WhatsApp group, alumni batch or circle of friends plants its own forest.
            Every tree you dedicate counts for your group. Which forest grows fastest?
          </p>
          {!loading && !error && (
            <p className="text-sritaru-200 text-sm mt-4">
              {forests.length} forests · {total.toLocaleString()} trees paid for or planted
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {[
            ["trees", "All years"],
            ["treesThisYear", `${CURRENT_YEAR} campaign`],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setSortBy(k)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                sortBy === k ? "bg-sritaru-600 text-white" : "bg-sritaru-100 text-sritaru-700 hover:bg-sritaru-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && <p className="text-center text-gray-500">Loading forests…</p>}
        {error && <p className="text-center text-red-600">{error}</p>}

        {!loading && !error && (
          <>
            <h2 className="text-xl font-semibold text-sritaru-800 mb-3">Group forests</h2>
            {leaderboard(groups)}
            <h2 className="text-xl font-semibold text-sritaru-800 mt-10 mb-1">Teams</h2>
            <p className="text-sm text-gray-600 mb-3">Led by our team captains since the 2025 campaign.</p>
            {leaderboard(teams)}
          </>
        )}

        <div className="bg-white rounded-xl shadow p-6 mt-8 text-center">
          <p className="font-semibold text-sritaru-800 mb-1">Want a forest for your group?</p>
          <p className="text-sm text-gray-600 mb-4">
            Send Bhaskar your group's name and we'll add it. Then share your forest's page in the group.
          </p>
          <Link
            to="/contribute"
            className="inline-block bg-sritaru-600 text-white px-6 py-2 rounded-lg hover:bg-sritaru-700 transition-colors"
          >
            Plant trees
          </Link>
        </div>
        <p className="text-xs text-gray-500 text-center mt-4">
          Counts are trees paid for or planted; pledges are shown separately. $1.80 or ₹150 a tree.
        </p>
      </div>
    </div>
  );
}

export default Forests;
